import type { FieldDef, Frame, TemplateDef, Values } from './types'

/** Escape untuk teks di dalam elemen maupun nilai atribut. */
export function esc(s: string): string {
  return s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;')
}

const WARNA = /^#[0-9a-f]{6}$/i
/** Hanya gambar raster sebagai data URL — tidak ada URL yang bisa dimuat dari luar. */
const GAMBAR = /^data:image\/(png|jpeg|webp);base64,[A-Za-z0-9+/]+=*$/
/**
 * Rujukan ke foto milik user di orisini-studio. Desain menyimpan INI, bukan
 * data URL-nya: foto bisa beberapa MB, dan desain disimpan ulang setiap kali
 * user mengetik. Rujukan diganti data URL tepat sebelum `buildDocument` —
 * di server saat render, di browser saat preview.
 */
export const ASSET_REF = /^asset:([0-9A-HJKMNP-TV-Z]{26})$/

/** ID aset yang dirujuk kolom gambar. */
export function assetIds(fields: readonly FieldDef[], values: Values): string[] {
  const ids: string[] = []
  for (const f of fields) {
    const m = f.type === 'image' ? ASSET_REF.exec(values[f.key] ?? '') : null
    if (m) ids.push(m[1]!)
  }
  return ids
}

/** Ganti rujukan aset dengan data URL; rujukan tanpa data dibuang (foto dihapus). */
export function resolveAssets(
  fields: readonly FieldDef[],
  values: Values,
  dataUrls: Record<string, string>,
): Values {
  const out = { ...values }
  for (const f of fields) {
    const m = f.type === 'image' ? ASSET_REF.exec(values[f.key] ?? '') : null
    if (!m) continue
    const url = dataUrls[m[1]!]
    if (url) out[f.key] = url
    else delete out[f.key]
  }
  return out
}

export class TemplateValueError extends Error {
  constructor(
    public readonly field: string,
    message: string,
  ) {
    super(message)
  }
}

/**
 * Validasi nilai mentah dari user terhadap kolom template. Kolom yang tidak
 * dikenal dibuang, kolom kosong diisi default.
 */
export function validateValues(
  fields: readonly FieldDef[],
  raw: Record<string, unknown>,
  opts: { draft?: boolean } = {},
): Values {
  const out: Values = {}
  for (const f of fields) {
    const v = raw[f.key]
    const s = typeof v === 'string' ? v.trim() : ''
    if (!s) {
      // Draf boleh belum lengkap — user sedang mengetik. Batas panjang, warna,
      // dan gambar tetap ditegakkan; kolom wajib baru ditagih saat ekspor.
      if (f.required && !opts.draft) throw new TemplateValueError(f.key, `${f.label} is required`)
      if (f.default !== undefined) out[f.key] = f.default
      continue
    }
    if ((f.type === 'text' || f.type === 'longtext' || f.type === 'list') && f.maxLength && s.length > f.maxLength) {
      throw new TemplateValueError(f.key, `${f.label} is too long (${f.maxLength} characters maximum)`)
    }
    if (f.type === 'color' && !WARNA.test(s)) {
      throw new TemplateValueError(f.key, `${f.label} must be a hex color like #1a1a1a`)
    }
    if (f.type === 'image' && !GAMBAR.test(s) && !ASSET_REF.test(s)) {
      throw new TemplateValueError(f.key, `${f.label} must be a PNG, JPEG, or WebP image`)
    }
    out[f.key] = s
  }
  return out
}

/**
 * Dokumen HTML lengkap untuk satu bingkai. Dokumen yang sama dipakai preview
 * di browser dan render di server — itulah yang menjamin yang dilihat user
 * sama dengan yang dirender.
 *
 * Halaman memberi tanda `data-ready` di <html> setelah font termuat dan script
 * template selesai; renderer menunggu tanda itu, bukan sekadar event load.
 */
export function buildDocument(t: TemplateDef, values: Values, frame: Frame, fontCss: string): string {
  // Pagar terakhir: yang masuk ke HTML hanya data URL raster. Rujukan aset yang
  // lupa di-resolve, atau URL yang lolos jalur lain, berhenti di sini.
  for (const f of t.fields) {
    const v = values[f.key]
    if (f.type === 'image' && v && !GAMBAR.test(v)) {
      throw new TemplateValueError(f.key, `${f.label} has not been loaded`)
    }
  }
  const { css, body, script } = t.render(values, frame)
  // Template tak tepercaya: hanya script ber-nonce (milik kita) yang jalan.
  const nonce = t.untrusted ? randomNonce() : null
  const scriptSrc = nonce ? `'nonce-${nonce}'` : "'unsafe-inline'"
  const attr = nonce ? ` nonce="${nonce}"` : ''
  return `<!doctype html>
<html lang="en"><head><meta charset="utf-8">
<meta http-equiv="Content-Security-Policy" content="default-src 'none'; img-src data:; font-src data:; style-src 'unsafe-inline'; script-src ${scriptSrc}">
<style>
${fontCss}
*,*::before,*::after{box-sizing:border-box;margin:0;padding:0}
html,body{width:${frame.width}px;height:${frame.height}px;overflow:hidden}
body{-webkit-font-smoothing:antialiased;text-rendering:geometricPrecision}
${css}
</style></head>
<body>${body}
${script ? `<script${attr}>${script}</script>` : ''}
<script${attr}>
document.fonts.ready.then(function(){
  var fit=window.__fit; if(typeof fit==='function') fit();
  document.documentElement.setAttribute('data-ready','1');
});
</script>
</body></html>`
}

/** Nonce CSP per dokumen. crypto.getRandomValues ada di Worker maupun browser. */
function randomNonce(): string {
  const b = new Uint8Array(16)
  crypto.getRandomValues(b)
  let s = ''
  for (const x of b) s += x.toString(16).padStart(2, '0')
  return s
}
